// —— Cudy: 绕开 Shopify load_feature 的自动加载机制 ——
// Shopify 的 load_feature 脚本带 MutationObserver：一旦 <model-viewer> 标签被插入 DOM，
// 就自动强载其内置的老引擎（导致 Three.js 重复导入警告 + NotSupportedError 注册冲突）。
// 对策：把 v3 引擎注册为别名子类 <cudy-model-viewer>，插入 DOM 前统一改名，
// Shopify 的侦测永远看不到 model-viewer 标签，自动加载彻底失活。
if (!customElements.get('cudy-model-viewer')) {
  const defineCudyAlias = () => {
    const base = customElements.get('model-viewer');
    if (base && !customElements.get('cudy-model-viewer')) {
      customElements.define('cudy-model-viewer', class extends base {});
    }
  };
  if (customElements.get('model-viewer')) {
    defineCudyAlias();
  } else {
    customElements.whenDefined('model-viewer').then(defineCudyAlias);
  }
}

// HTML 转义：热点文案来自 spec，可能含 & < > 引号等字符
const cudyEscapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// 缓存并移除 ProductJSON 数据脚本：ios-src（USDZ）改从内存匹配，
// 同时移除 ShopifyXR 自动加载机制的潜在触发源
const CudyProductModels = [];
document.querySelectorAll('[id^="ProductJSON-"]').forEach((el) => {
  try {
    const data = JSON.parse(el.textContent);
    if (Array.isArray(data)) CudyProductModels.push(...data);
  } catch (_) {
    /* noop */
  }
  el.remove();
});

if (!customElements.get('product-model')) {
  customElements.define(
    'product-model',
    class ProductModel extends DeferredMedia {
      constructor() {
        super();
      }

      // 接管 DeferredMedia.loadContent：唯一差别是把 <model-viewer> 改名为
      // <cudy-model-viewer> 后再插入 DOM（避免触发 Shopify 的老引擎自动加载）
      loadContent(focus = true) {
        window.pauseAllMedia();
        if (!this.getAttribute('loaded')) {
          const template = this.querySelector('template');
          if (template) {
            let node = template.content.firstElementChild.cloneNode(true);
            const isMediaTag = (el) => !!el && el.matches && el.matches('video, model-viewer, cudy-model-viewer, iframe');
            const legacy = isMediaTag(node) ? node : node.querySelector('model-viewer');

            if (legacy) {
              // 兜底注册别名（正常情况下顶层注册已完成）
              const base = customElements.get('model-viewer');
              if (base && !customElements.get('cudy-model-viewer')) {
                try {
                  customElements.define('cudy-model-viewer', class extends base {});
                } catch (_) {
                  /* 已注册过则忽略 */
                }
              }

              if (customElements.get('cudy-model-viewer')) {
                const renamed = document.createElement('cudy-model-viewer');
                for (const attr of legacy.attributes) renamed.setAttribute(attr.name, attr.value);
                while (legacy.firstChild) renamed.appendChild(legacy.firstChild);
                if (legacy === node) {
                  // legacy 是根节点（游离元素）：replaceWith 无效，直接替换引用
                  node = renamed;
                } else {
                  legacy.replaceWith(renamed);
                }
              } else {
                console.warn(
                  '[cudy] v3 引擎未注册，回退 model-viewer 原标签（Shopify 自动加载会介入）。hasV3=',
                  !!base
                );
              }
            }

            this.setAttribute('loaded', true);
            let deferredElement;
            if (isMediaTag(node)) {
              deferredElement = this.appendChild(node);
            } else {
              const content = document.createElement('div');
              content.appendChild(node);
              const found = content.querySelector('video, model-viewer, cudy-model-viewer, iframe');
              if (found) deferredElement = this.appendChild(found);
            }
            if (focus && deferredElement && deferredElement.focus) deferredElement.focus();
            if (
              deferredElement &&
              deferredElement.nodeName === 'VIDEO' &&
              deferredElement.getAttribute('autoplay')
            ) {
              // force autoplay for safari
              deferredElement.play();
            }
          }
        }
        this.applyCudyModelLighting();
        this.setupModernModelViewer();
      }

      applyCudyModelLighting() {
        const mv = this.querySelector('cudy-model-viewer, model-viewer');
        if (!mv) return;
        // 光线效果迁移自 product-3d-viewer.html 的更优光源配置（仅光照，不动其它行为）
        mv.setAttribute('exposure', '1');
        mv.setAttribute('environment-image', 'neutral');
        mv.setAttribute('shadow-intensity', '1');
        mv.setAttribute('shadow-softness', '1');
      }

      // 用 Google model-viewer v3.5.0 替换 Shopify 打包的老引擎（Dawn 2023 太老：
      // 右键被当左键 orbit、cameraTarget 异常）。引擎文件已本地化到 assets/model-viewer.min.js，
      // 由各 section 在 product-model.js 之前以 <script type="module"> 预载（走 Shopify CDN），
      // 并注册为 <cudy-model-viewer> 别名（见文件顶部说明）。
      setupModernModelViewer() {
        const mv = this.querySelector('cudy-model-viewer, model-viewer');
        if (!mv) return;

        mv.removeAttribute('disable-pan');
        // 移除 media_tag 默认加的 toggleable：它使 ModelViewerUI 在「单击视窗（非拖拽）」时
        // pause 模型并弹回未激活的 poster 状态（控件区也会跟着消失）。模型已由 reveal=auto
        // 自动激活，不应再退回未激活态；视频的 click 暂停不受影响（此处理仅针对 model）。
        mv.removeAttribute('toggleable');
        if (!mv.hasAttribute('camera-controls')) mv.setAttribute('camera-controls', '');
        // 阻止浏览器右键菜单，否则右键拖拽平移会被 contextmenu 打断
        mv.addEventListener('contextmenu', (e) => e.preventDefault());

        // AR 切换为 v3 原生实现：启用 ar 属性（ar-modes 默认即 webxr scene-viewer quick-look），
        // 模型查看器的 AR 按钮会按设备能力自动显隐（桌面端不出现），不再依赖 Shopify shopify-xr
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');

        const tag = customElements.get('cudy-model-viewer') ? 'cudy-model-viewer' : 'model-viewer';
        customElements.whenDefined(tag).then(() => {
          // 替代 Shopify ModelViewerUI.play() 的职责：确保模型脱离 poster 正常展示
          if (mv.getAttribute('reveal') === 'interaction') mv.setAttribute('reveal', 'auto');
          mv.classList.remove('disabled');
          mv.removeAttribute('tabindex');

          // iOS Quick Look 需要 USDZ 源：从缓存的 ProductJSON 数据里按 GLB 地址匹配
          this.setupNativeARSource(mv);

          // 自定义 AR 按钮（slot="ar-button"）：v3 默认 AR 按钮在部分场景不显示，
          // 自建按钮由 model-viewer 自动按设备能力显隐（不支持 AR 的环境自动隐藏）
          this.setupNativeARButton(mv);

          // 3D 热点标注（解释点 + 文案），toggle 按钮等 ModelViewerUI 就绪后补插
          this.setupHotspots(mv);

          // 新引擎原生右键平移可用；仅在检测到老 API 缺失时才启用手动平移兜底
          if (typeof mv.getCameraTarget !== 'function') {
            this.setupRightButtonPan(mv);
          }

          // 恢复原版控件（放大/缩小/全屏按钮 + 加载进度）
          this.setupModelViewerUI(mv);
        });
      }

      // 自定义 AR 按钮：slot="ar-button" 挂进 model-viewer 的 light DOM，
      // 点击由 model-viewer 原生接管（Android Scene Viewer / Google Lens、iOS Quick Look）。
      // 显隐采用"我们自己的能力模型"：Android（任意浏览器，含 Firefox，走 Scene Viewer
      // intent）和 iOS（Quick Look）视为有 AR 能力；桌面隐藏。
      // 注意：不能依赖 model-viewer 内置的 .enabled 判定——它在源码里硬编码把 Firefox
      // 排除出 Scene Viewer（Hh = Android && !Firefox && !OculusBrowser），而实测
      // Firefox Android 打开 Scene Viewer intent 是可用的。
      setupNativeARButton(mv) {
        if (mv.querySelector('[slot="ar-button"]')) return;

        const label = this.dataset.arButtonLabel || 'View in AR';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.slot = 'ar-button';
        btn.className = 'cudy-ar-button';
        btn.setAttribute('aria-label', label);
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
          '<path d="M12 2.7 20.8 7.4v9.2L12 21.3 3.2 16.6V7.4L12 2.7z"/>' +
          '<path d="M3.2 7.4 12 12l8.8-4.6M12 12v9.3"/></svg>';
        const span = document.createElement('span');
        span.className = 'cudy-ar-button__label';
        span.textContent = label;
        btn.appendChild(span);

        mv.appendChild(btn);

        // 我们的能力模型：Android / iOS 强制显示（对抗 model-viewer 的保守判定）；
        // 桌面等无 .enabled 且非移动 UA 的环境维持隐藏。
        const ua = navigator.userAgent || '';
        const isMobileARCandidate =
          /Android|iPad|iPhone|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
        if (isMobileARCandidate && mv.shadowRoot) {
          const style = document.createElement('style');
          style.textContent = '.slot.ar-button:not(.enabled) { display: block !important; }';
          mv.shadowRoot.appendChild(style);
        }

        // 点击兜底：model-viewer 原生可处理时（.slot.ar-button 有 .enabled）交给它；
        // 否则（如 Android Firefox）手动构造 Scene Viewer intent 拉起 Google Lens，
        // 与旧 ShopifyXR 按钮的底层行为一致。
        btn.addEventListener('click', () => {
          try {
            const slot = mv.shadowRoot && mv.shadowRoot.querySelector('.slot.ar-button');
            if (slot && slot.classList.contains('enabled')) return; // 原生接管
            if (!/Android/i.test(navigator.userAgent || '')) return;

            const src = mv.getAttribute('src');
            if (!src) return;
            const glb = new URL(src, window.location.href).toString();
            const title = String(mv.getAttribute('alt') || document.title || '3D Model').slice(0, 100);

            const sv = new URL('https://arvr.google.com/scene-viewer/1.2');
            sv.searchParams.set('file', glb);
            sv.searchParams.set('mode', 'ar_preferred');
            sv.searchParams.set('title', title);

            const fallback = encodeURIComponent(sv.toString());
            window.location.href =
              'intent://' +
              sv.host +
              sv.pathname +
              sv.search +
              '#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=' +
              fallback +
              ';end;';
          } catch (e) {
            console.warn('[cudy] Scene Viewer launch failed:', e);
          }
        });
      }

      // 为 v3 原生 AR 补 ios-src（USDZ）：ProductJSON 数据里带有每个模型的
      // sources（glb + usdz），按当前 model-viewer 的 src（GLB 地址）匹配后设置 ios-src。
      setupNativeARSource(mv) {
        try {
          if (mv.getAttribute('ios-src')) return;
          if (!CudyProductModels.length) return;

          const src = mv.getAttribute('src') || '';
          const glbName = src.split('/').pop().split('?')[0];
          const entry =
            CudyProductModels.find((m) =>
              (m.sources || []).some(
                (s) => s.url === src || s.url.split('/').pop().split('?')[0] === glbName
              )
            ) || null;
          if (!entry) return;

          const usdz = (entry.sources || []).find(
            (s) => /usdz/i.test(s.format || '') || /\.usdz(\?|$)/i.test(s.url || '')
          );
          if (usdz && usdz.url) mv.setAttribute('ios-src', usdz.url);
        } catch (e) {
          console.warn('[cudy] ios-src setup failed:', e);
        }
      }

      // —— Cudy: 3D 热点标注（spec「N/T 3D Model Tracking-hide-on-web」）——
      // 数据源：main-product-cudy.liquid 输出的 <script type="application/json" id="CudyHotspots-*">，
      // 坐标为归一化 0~1（相对模型包围盒），直接交给 model-viewer 原生 data-surface 吸附表面；
      // 配合 data-visibility-attribute 由引擎按表面法线朝向切换 data-visible，背面自动隐藏。
      setupHotspots(mv) {
        const attach = () => {
          if (mv.querySelector('.cudy-hotspot')) return; // 幂等：画廊/移动端模组各一份 viewer，互不干扰
          let data = null;
          const tag = document.querySelector('script[type="application/json"][id^="CudyHotspots-"]');
          if (tag) {
            try {
              data = JSON.parse(tag.textContent);
            } catch (e) {
              console.warn('[cudy] hotspots JSON parse failed:', e);
            }
          }
          if (!Array.isArray(data)) return;

          // 归一化 0~1（相对包围盒）→ 模型本地米坐标。
          // 注意：data-surface 不是归一化坐标，它要求 surfaceFromPoint() 输出的
          // 8 数字格式（mesh/顶点索引+重心坐标），直接塞 x y z 会导致解析失败、
          // 所有热点退回原点叠在一起。这里先按米坐标落位，再二次吸附到真实表面。
          let dims = null, center = null;
          try {
            dims = mv.getDimensions();
            center = mv.getBoundingBoxCenter();
          } catch (e) { /* 模型未就绪，跳过 */ }
          if (!dims || !center) return;

          data.forEach((p, i) => {
            if (!p || typeof p.x !== 'number' || typeof p.y !== 'number' || typeof p.z !== 'number') return;
            const el = document.createElement('div');
            el.className = 'cudy-hotspot';
            el.slot = 'hotspot-cudy-' + i; // 必须 hotspot 开头才会被 model-viewer 识别为热点
            const mx = center.x + (p.x - 0.5) * dims.x;
            const my = center.y + (p.y - 0.5) * dims.y;
            const mz = center.z + (p.z - 0.5) * dims.z;
            el.dataset.position = mx.toFixed(4) + 'm ' + my.toFixed(4) + 'm ' + mz.toFixed(4) + 'm';
            // 近似法线：包围盒中心指向点位（径向近似），供引擎做朝向显隐；
            // 后续吸附成功后会被真实表面法线覆盖
            let dx = mx - center.x, dy = my - center.y, dz = mz - center.z;
            const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
            el.dataset.normal = (dx / len).toFixed(4) + ' ' + (dy / len).toFixed(4) + ' ' + (dz / len).toFixed(4);
            el.dataset.visibilityAttribute = 'visible';
            let inner = '<span class="cudy-hotspot__dot"></span>';
            if (p.text) inner += '<span class="cudy-hotspot__label">' + cudyEscapeHtml(p.text) + '</span>';
            el.innerHTML = inner;
            mv.appendChild(el);
          });
          if (mv.querySelector('.cudy-hotspot')) {
            this.setupHotspotToggle(mv);
            this.snapHotspotsToSurface(mv);
          }
        };
        if (mv.loaded) attach();
        else mv.addEventListener('load', attach, { once: true });
      }

      // 二次校准：把热点从「包围盒插值点」吸附到「真实网格表面」。
      // 流程：先用 data-position 渲染一帧 → queryHotspot 拿屏幕坐标 →
      // surfaceFromPoint 求命中表面的 8 数字 surface → updateHotspot 吸附。
      // 吸附成功后位置精确贴面、法线由引擎自动计算（朝向显隐也随之准确）；
      // 射线未命中（如点位落在凹腔）则静默降级，保留插值点位置。
      snapHotspotsToSurface(mv) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!mv.loaded || typeof mv.queryHotspot !== 'function' || typeof mv.surfaceFromPoint !== 'function') return;
          mv.querySelectorAll('.cudy-hotspot').forEach((el) => {
            const name = el.slot;
            try {
              const h = mv.queryHotspot(name);
              if (!h || !h.canvasPosition) return;
              const surface = mv.surfaceFromPoint(h.canvasPosition.x, h.canvasPosition.y);
              if (surface) mv.updateHotspot({ name: name, surface: surface });
            } catch (e) { /* 降级：保留 data-position 位置 */ }
          });
        }));
      }

      // 标注显隐 toggle：桌面挂在 Shopify ModelViewerUI 控件堆叠（右下角）末尾，
      // 与放大/全屏按钮同款样式；移动端 Shopify 控件区整体 display:none，
      // 改用独立浮动按钮，放右侧、与「View in AR」按钮同高（见 CSS 断点规则）。
      setupHotspotToggle(mv) {
        const mq = window.matchMedia('(min-width: 750px)'); // Dawn 移动端断点
        let btn = null;
        const place = () => {
          const ui = mv.closest('.shopify-model-viewer-ui');
          if (!ui) return false; // ModelViewerUI 尚未建包装层，稍后重试
          if (!btn) {
            btn = ui.querySelector('.cudy-hotspot-toggle') ||
              (mv.parentElement && mv.parentElement.querySelector('.cudy-hotspot-toggle'));
          }
          if (!btn) btn = this.buildHotspotToggle(mv);
          if (mq.matches) {
            const area = ui.querySelector('.shopify-model-viewer-ui__controls-area');
            if (!area) return false; // 控件区未就绪，稍后重试
            btn.classList.remove('cudy-hotspot-toggle--standalone');
            btn.classList.add('shopify-model-viewer-ui__button', 'shopify-model-viewer-ui__button--control');
            area.appendChild(btn); // 堆叠末位 = 控件右下角
          } else {
            btn.classList.remove('shopify-model-viewer-ui__button', 'shopify-model-viewer-ui__button--control');
            btn.classList.add('cudy-hotspot-toggle--standalone');
            ui.appendChild(btn); // 移动端：独立浮动按钮
          }
          return true;
        };
        if (place()) return;
        // ModelViewerUI 异步初始化，稍等重试；仍未就绪则降级为独立定位按钮
        setTimeout(() => {
          if (place()) return;
          const host = mv.closest('.shopify-model-viewer-ui') || mv.parentElement;
          if (!host || host.querySelector('.cudy-hotspot-toggle')) return;
          btn = this.buildHotspotToggle(mv, true);
          host.appendChild(btn);
        }, 800);
        // 断点切换（旋转屏幕/缩放窗口）时迁移到对应位置
        try { mq.addEventListener('change', place); }
        catch (_) { mq.addListener(place); } // 旧 Safari
      }

      buildHotspotToggle(mv, standalone) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cudy-hotspot-toggle' + (standalone ? ' cudy-hotspot-toggle--standalone' : '');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Toggle annotations');
        btn.innerHTML =
          '<svg class="cudy-hotspot-toggle__icon-on" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
          '<path d="M20.6 13.3 13.3 20.6a1.9 1.9 0 0 1-2.7 0L3.4 13.4a1.9 1.9 0 0 1-.6-1.4V4.7c0-1 .9-1.9 1.9-1.9H12c.5 0 1 .2 1.4.6l7.2 7.2c.8.7.8 2 0 2.7z"/>' +
          '<circle cx="8" cy="8" r="1.4"/>' +
          '<circle cx="15.2" cy="13.2" r="1.4" fill="currentColor" stroke="none"/>' +
          '</svg>' +
          '<svg class="cudy-hotspot-toggle__icon-off" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
          '<path d="M20.6 13.3 13.3 20.6a1.9 1.9 0 0 1-2.7 0L3.4 13.4a1.9 1.9 0 0 1-.6-1.4V4.7c0-1 .9-1.9 1.9-1.9H12c.5 0 1 .2 1.4.6l7.2 7.2c.8.7.8 2 0 2.7z"/>' +
          '<circle cx="8" cy="8" r="1.4"/>' +
          '<circle cx="15.2" cy="13.2" r="1.4" fill="currentColor" stroke="none"/>' +
          '<path d="M4 20 20 4"/>' +
          '</svg>';
        btn.addEventListener('click', (e) => {
          e.stopPropagation(); // 别触发查看器的播放/轨道交互
          const off = !mv.classList.contains('cudy-hotspots-off');
          mv.classList.toggle('cudy-hotspots-off', off);
          btn.classList.toggle('is-off', off);
          btn.setAttribute('aria-pressed', String(!off));
        });
        return btn;
      }

      // 恢复 Shopify ModelViewerUI 的原版控件（放大/缩小/全屏按钮）。
      // 已核实：它只调用 model-viewer 公开 API（fieldOfView/play/pause/cameraOrbit），
      // 全屏作用于它自建的包装容器（div.shopify-model-viewer-ui），不碰 shadow DOM，
      // 因此与 v3 新引擎完全兼容。加载失败不影响查看器本体。
      setupModelViewerUI(mv) {
        if (this.modelViewerUI) return;
        Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              if (errors) return;
              try {
                this.modelViewerUI = new Shopify.ModelViewerUI(mv);
                // 控件区（右下角按钮堆叠）此刻已就绪，插入标注 toggle
                this.setupHotspotToggle(mv);
              } catch (e) {
                console.warn('[cudy] ModelViewerUI init failed:', e);
              }
            },
          },
        ]);
      }

      // Shopify 打包的 model-viewer 会把右键当左键 orbit，所以自己实现右键平移：
      // 1) host 捕获阶段拦截右键 pointerdown（阻止传进 shadow 内部 canvas 被 orbit）；
      // 2) 在 window 上跟踪拖拽，按"米/像素"换算平移 cameraTarget。
      // 兼容性优先：所有 model-viewer API 都带兜底，出错打印 [cudy-pan] 警告。
      setupRightButtonPan(mv) {
        let panning = false;
        let lastX = 0;
        let lastY = 0;
        let base = { x: 0, y: 0, z: 0 };

        // 读取当前相机目标点：优先 getCameraTarget()，失败则解析 cameraTarget 字符串
        const readTarget = () => {
          try {
            if (typeof mv.getCameraTarget === 'function') {
              const t = mv.getCameraTarget();
              if (t && typeof t.x === 'number') return { x: t.x, y: t.y, z: t.z };
            }
          } catch (_) {
            /* 落到字符串解析 */
          }
          const s = String(mv.cameraTarget || '').trim();
          if (s && s !== 'auto') {
            const parts = s.split(/[\s,]+/).map(Number);
            if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
              return { x: parts[0], y: parts[1], z: parts[2] };
            }
          }
          return { x: 0, y: 0, z: 0 };
        };

        mv.addEventListener(
          'pointerdown',
          (e) => {
            if (e.button !== 2) return;
            e.preventDefault();
            e.stopPropagation(); // 别让 model-viewer 把右键拖拽当成 orbit
            panning = true;
            lastX = e.clientX;
            lastY = e.clientY;
            base = readTarget(); // 按下时快照，之后纯靠位移累加，不回读
          },
          true
        );

        window.addEventListener('pointermove', (e) => {
          if (!panning) return;
          if ((e.buttons & 2) === 0) {
            panning = false; // 右键已松开
            return;
          }

          const dx = e.clientX - lastX;
          const dy = e.clientY - lastY;
          lastX = e.clientX;
          lastY = e.clientY;
          if (dx === 0 && dy === 0) return;

          try {
            const orbit = typeof mv.getCameraOrbit === 'function' ? mv.getCameraOrbit() : null;
            const radius = orbit && orbit.radius ? orbit.radius : 2;
            const fov = typeof mv.getFieldOfView === 'function' ? mv.getFieldOfView() || 30 : 30;
            const metersPerPixel =
              (2 * radius * Math.tan((fov * Math.PI) / 360)) / (mv.clientHeight || 1);

            const theta = ((orbit ? orbit.theta : 0) * Math.PI) / 180;
            const phi = ((orbit ? orbit.phi : 75) * Math.PI) / 180;
            // 相机屏幕空间的"右 / 上"方向（世界坐标）
            const rightX = Math.cos(theta);
            const rightZ = -Math.sin(theta);
            const upY = Math.sin(phi);
            const upZ = -Math.cos(phi);

            // 拖右 → 目标点左移（模型跟着右移）；拖下 → 目标点上移（模型跟着下移）
            let nx = base.x - rightX * dx * metersPerPixel;
            let ny = base.y + upY * dy * metersPerPixel;
            let nz = base.z - rightZ * dx * metersPerPixel + upZ * dy * metersPerPixel;

            // 防呆：限制目标点距原点不超过轨道半径，避免把模型彻底拖丢
            const dist = Math.sqrt(nx * nx + ny * ny + nz * nz);
            if (dist > radius && dist > 0) {
              const s = radius / dist;
              nx *= s;
              ny *= s;
              nz *= s;
            }

            mv.cameraTarget = nx + ' ' + ny + ' ' + nz;
            // 老版本没有插值动画时的立即生效兜底（新版本忽略即可）
            if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
          } catch (err) {
            console.warn('[cudy-pan] pan failed:', err);
            panning = false;
          }
        });

        const endPan = () => {
          panning = false;
        };
        window.addEventListener('pointerup', endPan);
        window.addEventListener('pointercancel', endPan);
        window.addEventListener('blur', endPan);
      }
    }
  );
}

window.ProductModel = {
  // Cudy: AR 已切换为 model-viewer v3 原生实现（ar 属性 + ios-src），
  // 不再加载 Shopify 'shopify-xr'（其内部捆绑的老引擎会与新引擎抢注冲突）。
  // 保留空实现以兼容 quick-add.js 等外部调用点。
  loadShopifyXR() {},
};

// —— Cudy: 移动端全屏模组打开时自动加载当前激活的 3D 模型 ——
// 旧行为需要二次点击（模组里再点一次 3D 按钮）；桌面端保持点击加载不变，
// 避免打开模组就一次性加载全部模型浪费流量。
// 触发采用三重保险：模组 open/class 属性变化（含子树，覆盖激活切换）、
// 文档级点击兜底（0ms + 300ms 延迟，等 open/active 就位）。loadContent 幂等可安全重入。
const cudyAutoLoadMobileModal = () => {
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  document.querySelectorAll('product-modal .product-media-modal__content, .product-media-modal > .product-media-modal__content').forEach((content) => {
    const modal = content.closest('product-modal, .product-media-modal');
    if (modal && !modal.hasAttribute('open')) return;
    // cudy 模组的 .active 落在 [data-media-id] 元素上（img / .product-media-modal__model / deferred-media），
    // 不在其直接子元素（.media-wrapper-cudy）上，所以按 [data-media-id].active 判断
    const activeMedia = content.querySelector('[data-media-id].active');
    content.querySelectorAll('product-model').forEach((pm) => {
      if (pm.getAttribute('loaded')) return;
      const host = pm.closest('[data-media-id]');
      if (activeMedia && (!host || !host.classList.contains('active'))) return;
      pm.loadContent(false);
    });
  });
};
{
  const modals = new Set();
  document.querySelectorAll('product-modal, .product-media-modal').forEach((m) => modals.add(m));
  modals.forEach((modal) => {
    new MutationObserver(cudyAutoLoadMobileModal).observe(modal, {
      attributes: true,
      attributeFilter: ['open', 'class'],
      subtree: true,
    });
  });
  document.addEventListener('click', () => {
    setTimeout(cudyAutoLoadMobileModal, 0);
    setTimeout(cudyAutoLoadMobileModal, 350);
  });
}
